'use client'
import React, {
  FunctionComponent,
  useEffect,
  useState,
  useCallback
} from 'react'
import classNames from 'classnames'
import { RelationContact } from '@/services/mobi-api/packageShift/types'
import Step1 from './step1'
import Step2 from './step2'
import Step3 from './step3'
import Step4 from './step4'
import Step5 from './step5'
import { ChoosenInfo, ContactsV2 } from '@/services/mobi-api/numberv2/types'
import FaceKycStep from './faceKycStep'
import { trackNewNumberEvent } from '@/utils/newNumberFlowTracking'

export interface Obj {
  regno?: string
  channel?: 'sms' | 'email' | 'app'
  instanceId?: string
  email?: string
  choosenPackage?: string
  choosenInfo?: ChoosenInfo
  choosenNumber?: string
  choosenSim?: string
  contactInfo?: ContactsV2[]
  rateplan?: string
  kycRedirectUrl?: string
  method?: 'emongolia' | 'kyc'
  kycMethod?: 'register' | 'id' | 'passport'
  emongoliaMethod?: 'message' | 'email' | 'notification'
  service?: 'digiConsent' | 'newNumber' | 'packageShift' | 'handset' | 'simRecovery'
  sessionId?: string
}

interface Props {
  instanceId?: string
  contacts?: RelationContact[]
  service?: 'digiConsent' | 'newNumber' | 'packageShift' | 'handset' | 'simRecovery'
  onComplete?: (instance?: any) => void
}

enum SubStep {
  Choose = 0,
  Register = 1,
  Emongolia = 2,
  OtpVerification = 3,
  FaceKyc = 5,
  SuccessPage = 4,
}

const EMongoliaOtp: FunctionComponent<Props> = ({
  instanceId,
  service,
  onComplete
}) => {
  const [currentSubStep, setCurrentSubStep] = useState<SubStep>(SubStep.Choose)
  const [objs, setObj] = useState<Obj | undefined>()
  const startedAtRef = React.useRef(Date.now())

  const merge = useCallback((patch: Partial<Obj>) => {
    setObj((prev) => ({ ...prev, ...patch }))
  }, [])

  useEffect(() => {
    if (service !== 'newNumber') {
      return
    }

    trackNewNumberEvent('page_view_step', {
      step_name: 'identity_verification'
    })
    startedAtRef.current = Date.now()

    return () => {
      trackNewNumberEvent('time_spent_step', {
        step_name: 'identity_verification',
        time_spent_seconds: Math.max(
          1,
          Math.round((Date.now() - startedAtRef.current) / 1000)
        )
      })
    }
  }, [service])

  const goEmongolia = (): void => setCurrentSubStep(SubStep.Emongolia)
  const goFaceKyc = (): void => setCurrentSubStep(SubStep.FaceKyc)
  const goRegister = (): void => setCurrentSubStep(SubStep.Register)
  const goOtpVerification = (): void => setCurrentSubStep(SubStep.OtpVerification)
  const goSuccessPage = (): void => setCurrentSubStep(SubStep.SuccessPage)

  const screens = [
    {
      body: (
        <Step1
          onChooseEmongolia={(method) => {
            merge({ method })
            goRegister()
          }}
          service={service}
        />
      )
    },
    {
      body: (
        <Step2
          onContinue={({ regno }) => {
            merge({ regno })
            if (objs?.method === 'emongolia') {
              goEmongolia()
            } else {
              goFaceKyc()
            }
          }}
          instanceId={objs?.instanceId ?? instanceId}
        />
      )
    },
    {
      body: (
        <Step3
          regno={objs?.regno ?? ''}
          onContinue={({ channel }) => {
            merge({ channel })
            goOtpVerification()
          }}
        />
      )
    },
    {
      body: (
        <Step4
          regno={objs?.regno ?? ''}
          channel={objs?.channel as 'email' | 'sms' | 'app'}
          onContinue={goSuccessPage}
        />
      )
    },
    {
      body: (
        <Step5
          instanceId={objs?.instanceId ?? instanceId}
          regno={objs?.regno}
          onBack={goOtpVerification}
          service={service}
          onComplete={onComplete}
        />
      )
    },
    {
      body: (
        <FaceKycStep
          regno={objs?.regno ?? ''}
          onContinue={goSuccessPage}
          instanceId={instanceId}
          onBackToChoose={() => setCurrentSubStep(SubStep.Choose)}
        />
      )
    }
  ]

  return (
    <div className='md:flex h-screen items-center justify-center'>
      {screens.map((s, index) => (
        <div key={index}>
          <span
            className={classNames(
              currentSubStep !== index
                ? 'text-[#6E7882] font-bold'
                : 'font-extrabold text-textHigh'
            )}
          />
          {currentSubStep === index ? <div>{s.body}</div> : <div />}
        </div>
      ))}
    </div>
  )
}

export default EMongoliaOtp
